import { useRef, useState } from 'react';
import type { JSX } from 'react';

import { currentIsoWeek, FACHLICHE_ZEITZONE } from '@asas/domain/iso-week';
import type {
  BelegungZelle,
  FachgruppeList,
  IsoWeek,
  WeekGrid,
  WeekGridCellBreakdown,
  TenantSettings,
} from '@asas/domain';

import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { erzeugeSchreibwarteschlange } from '../daten/warteschlange';
import { sperrtWeitereVersuche } from '../fachgruppen/fachgruppen';
import { trpc } from '../trpc';
import { OffeneAnfragen } from '../anfragen/AnfrageBereich';

import { auftragEingabe, pruefePlanungsauftrag, ueberlastHinweis } from './planung';
import type { Planungsauftrag } from './planung';
import { fensterVerschieben, wochenkopf } from './wochenraster';
import { Aufschluesselung, WochenrasterTabelle } from './WochenrasterAnsicht';
import type { ZellenAuswahl } from './WochenrasterAnsicht';
import { ZellePlanen } from './ZellePlanen';
import type { Planungswarteschlange } from './ZellePlanen';

export interface WochenrasterBereichProps {
  /**
   * Bietet die Ansicht das Raster an (`darfWochenrasterLesen`)? Ohne
   * `resource_allocation:read` nicht — der erste Klick schriebe sonst ein 403
   * ins Audit-Log.
   */
  readonly darfLesen: boolean;
  /**
   * Bietet die Ansicht die offenen Anfragen der Fachgruppe an (AC-404, AK-3)?
   * Nur mit `resource_allocation:approve` (`darfOffeneAnfragenLesen`).
   */
  readonly darfOffeneAnfragen?: boolean;
  /** AC-405: „Entscheiden" je offener Anfrage (`darfEntscheiden`). */
  readonly darfEntscheiden?: boolean;
  /**
   * AC-403: Belegungen einer Zelle bearbeiten (`darfPlanen`,
   * `resource_allocation:write`). Braucht `inhaber` für die tab-gebundene
   * Schreibwarteschlange (AC-409).
   */
  readonly planen?: { readonly inhaber: string; readonly nachtragen: boolean };
}

/** Ein versionierter Planungsauftrag an den Server; `art` geht nicht mit. */
async function planungSenden(auftrag: Planungsauftrag) {
  switch (auftrag.art) {
    case 'aendern':
      return await trpc.belegungen.aendern.mutate(auftragEingabe(auftrag));
    case 'verschieben':
      return await trpc.belegungen.verschieben.mutate(auftragEingabe(auftrag));
    case 'loeschen':
      return await trpc.belegungen.loeschen.mutate(auftragEingabe(auftrag));
  }
}

/** Welches Fenster gerade laedt oder angezeigt wird. */
interface Fenster {
  readonly fachgruppeId: string;
  readonly from: IsoWeek;
}

function zellenSchluessel(ziel: ZellenAuswahl): string {
  return `${ziel.employeeId}|${String(ziel.isoYear)}-${String(ziel.isoWeek)}`;
}

function fensterSchluessel(fenster: Fenster): string {
  return `${fenster.fachgruppeId}|${String(fenster.from.isoYear)}-${String(fenster.from.isoWeek)}`;
}

/**
 * Das Wochenraster der eigenen Fachgruppe (AC-402) — und seit AC-403 das
 * Planen in einer Zelle, wenn `planen` gesetzt ist (`ZellePlanen`).
 *
 * GELADEN WIRD ERST AUF ANFORDERUNG (keine Effekte, Muster
 * `FachgruppenBereich`): erst die Fachgruppen und die Modulliste, das Raster
 * nach der Auswahl einer Fachgruppe, die Aufschluesselung bzw. die
 * bearbeitbaren Belegungen nach dem Klick auf eine Zelle. Der einzige Effekt
 * ist die Schreibwarteschlange (AC-409): sie meldet sich beim Abbau ab.
 *
 * FENSTERWECHSEL WAEHREND DES LADENS bricht den laufenden Aufruf WIRKLICH ab:
 * `wochenraster.raster` laeuft ueber eine eigene, ungebuendelte Link-Kette
 * (`trpc.ts`, E11), in der ein `AbortSignal` wirkt. Eine trotzdem eintreffende
 * Antwort fuer ein ueberholtes Fenster wird verworfen. Die Auswahl bleibt
 * waehrend des Ladens offen (anders als in `MitarbeiterBereich`): ein
 * Fensterwechsel schliesst die bearbeitete Zelle, fremde Werte koennen in kein
 * Formular geraten. Das Formular der Zelle selbst ist gesperrt, solange neu
 * geladen wird (AC-403).
 */
export function WochenrasterBereich({
  darfLesen,
  darfOffeneAnfragen = false,
  darfEntscheiden = false,
  planen,
}: WochenrasterBereichProps): JSX.Element {
  const [liste, setListe] = useState<FachgruppeList | null>(null);
  const [modulAktiv, setModulAktiv] = useState(true);
  const [laedtListe, setLaedtListe] = useState(false);
  const [listenFehler, setListenFehler] = useState<FehlerBefund | null>(null);
  const [fenster, setFenster] = useState<Fenster | null>(null);
  const [raster, setRaster] = useState<WeekGrid | null>(null);
  const [laedtRaster, setLaedtRaster] = useState(false);
  const [rasterFehler, setRasterFehler] = useState<FehlerBefund | null>(null);
  const [auswahl, setAuswahl] = useState<ZellenAuswahl | null>(null);
  const [zelle, setZelle] = useState<WeekGridCellBreakdown | null>(null);
  const [zellenFehler, setZellenFehler] = useState<FehlerBefund | null>(null);
  const [planZelle, setPlanZelle] = useState<BelegungZelle | null>(null);
  /**
   * AC-403 (Review B1): die Zelle laedt. Solange ist ihr Formular gesperrt —
   * sonst stuenden beim Wechsel die Belegungen der alten Zelle unter dem neuen
   * Namen, oder nach dem Speichern Felder mit der alten Version.
   */
  const [laedtZelle, setLaedtZelle] = useState(false);
  const [hinweis, setHinweis] = useState('');
  const laufend = useRef<AbortController | null>(null);
  /** Das zuletzt angeforderte Fenster — `nachAenderung` laedt nie ein ueberholtes (Review N4). */
  const fensterAktuell = useRef<Fenster | null>(null);
  const rasterFuer = useRef<string | null>(null);
  const zelleFuer = useRef<string | null>(null);

  // AC-403: EINE Schreibwarteschlange je Bereich, erst beim ersten Bedarf
  // angelegt (kein Effekt: beim Seitenaufruf geschieht nichts). Sie ist an
  // `planen.inhaber` gebunden; `App` setzt dafuer `key` auf das Subjekt. Ihr
  // `online`-Abonnement lebt so lange wie die Seite — gewollt: ein wartender
  // Wert geht auch dann ab, wenn die Zelle schon geschlossen ist (AC-409, AK 4).
  const warteschlangeRef = useRef<Planungswarteschlange | null>(null);

  function planungswarteschlange(inhaber: string): Planungswarteschlange {
    warteschlangeRef.current ??= erzeugeSchreibwarteschlange<
      Planungsauftrag,
      Awaited<ReturnType<typeof planungSenden>>
    >({
      name: 'belegungen',
      senden: planungSenden,
      pruefe: pruefePlanungsauftrag,
      entwuerfe: erzeugeEntwurfsspeicher({ inhaber }),
      melde: (_schluessel, ausgang) => {
        // Review S2: auch eine nachgereichte Eingabe ueber der Schwelle (AK 6)
        // speichert der Server erst nach Bestaetigung — dann ist NICHTS gespeichert.
        if (ausgang.art === 'gespeichert' && ausgang.ergebnis?.ergebnis === 'bestaetigung_noetig') {
          setHinweis(
            `Eine wartende Eingabe über ${String(ausgang.ergebnis.schwelleStunden)} Stunden wurde nicht gespeichert — bitte erneut eingeben und bestätigen.`,
          );
        } else if (ausgang.art === 'gespeichert' || ausgang.art === 'bereits_gespeichert') {
          setHinweis('Eine wartende Eingabe wurde gesendet; bitte neu laden.');
        }
      },
    });

    return warteschlangeRef.current;
  }

  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );
  const gesperrt =
    sperrtWeitereVersuche(listenFehler) ||
    sperrtWeitereVersuche(rasterFehler) ||
    sperrtWeitereVersuche(zellenFehler);

  async function oeffnen(): Promise<void> {
    setLaedtListe(true);
    setListenFehler(null);

    try {
      const [fachgruppen, einstellungen]: [FachgruppeList, TenantSettings] = await Promise.all([
        trpc.fachgruppen.list.query(),
        trpc.tenant.settings.query(),
      ]);

      setListe(fachgruppen);
      setModulAktiv(einstellungen.modules.ressourcenplanung === true);
    } catch (error) {
      setListenFehler(klassifiziereFehler(error, { schreibzugriff: false }));
    } finally {
      setLaedtListe(false);
    }
  }

  /**
   * Laedt das Raster eines Fensters. `auswahlBehalten` (AC-403): nach dem
   * Speichern bleibt die Zelle offen; das neue Raster kommt zurueck, damit der
   * Aufrufer daraus die Ueberlast liest (AK 4).
   */
  async function rasterLaden(
    ziel: Fenster,
    { auswahlBehalten = false }: { readonly auswahlBehalten?: boolean } = {},
  ): Promise<WeekGrid | null> {
    laufend.current?.abort();
    const abbruch = new AbortController();
    laufend.current = abbruch;
    const schluessel = fensterSchluessel(ziel);
    rasterFuer.current = schluessel;
    fensterAktuell.current = ziel;

    setFenster(ziel);
    // Eine andere Fachgruppe: das alte Raster gehoert nicht mehr zur Auswahl.
    setRaster((alt) => (alt?.fachgruppeId === ziel.fachgruppeId ? alt : null));
    setLaedtRaster(true);
    setRasterFehler(null);
    if (!auswahlBehalten) {
      setAuswahl(null);
      setZelle(null);
      setPlanZelle(null);
      setLaedtZelle(false);
      setZellenFehler(null);
      setHinweis('');
      zelleFuer.current = null;
    }

    try {
      const neu = await trpc.wochenraster.raster.query(
        { fachgruppeId: ziel.fachgruppeId, from: ziel.from },
        { signal: abbruch.signal },
      );

      if (rasterFuer.current !== schluessel) return null;
      setRaster(neu);
      return neu;
    } catch (error) {
      if (rasterFuer.current !== schluessel) return null;

      const befund = klassifiziereFehler(error, { schreibzugriff: false });

      if (befund.art !== 'abgebrochen') {
        setRasterFehler(befund);
        setRaster(null);
      }
      return null;
    } finally {
      if (rasterFuer.current === schluessel) setLaedtRaster(false);
    }
  }

  /**
   * Nach einer Aenderung (AC-403, AK 1, AK 4): Raster und Zelle neu laden —
   * Summen und Auslastung rechnet der Server — und Ueberlast in den
   * betroffenen Wochen melden, ohne etwas zu blockieren.
   */
  async function nachAenderung(ziel: ZellenAuswahl, wochen: readonly IsoWeek[]): Promise<void> {
    const imFenster = fensterAktuell.current;
    // Review N4: die Zelle ist inzwischen eine andere oder das Fenster
    // gewechselt — dann gilt deren Laden, nicht ein Neuladen des alten Stands.
    if (imFenster === null || zelleFuer.current !== zellenSchluessel(ziel)) return;

    setLaedtZelle(true);
    const neu = await rasterLaden(imFenster, { auswahlBehalten: true });
    if (neu === null) {
      setLaedtZelle(false);
      return;
    }
    await zelleLaden(ziel, { behalten: true });

    if (wochen.length > 0) {
      setHinweis(ueberlastHinweis(neu, ziel.employeeId, wochen) ?? 'Gespeichert.');
    }
  }

  async function zelleLaden(
    ziel: ZellenAuswahl,
    { behalten = false }: { readonly behalten?: boolean } = {},
  ): Promise<void> {
    const imFenster = fensterAktuell.current;
    if (imFenster === null) return;

    const schluessel = zellenSchluessel(ziel);
    zelleFuer.current = schluessel;
    setAuswahl(ziel);
    setZellenFehler(null);
    setLaedtZelle(true);
    // Review B1: eine ANDERE Zelle zeigt nie die Belegungen der vorigen.
    if (!behalten) {
      setPlanZelle(null);
      setHinweis('');
    }
    const eingabe = {
      fachgruppeId: imFenster.fachgruppeId,
      employeeId: ziel.employeeId,
      woche: { isoYear: ziel.isoYear, isoWeek: ziel.isoWeek },
    };

    try {
      // AC-403: wer plant, bekommt die bearbeitbaren Belegungen mit Projekt (H1);
      // alle anderen die Aufschluesselung aus AC-402.
      if (planen !== undefined) {
        const neu = await trpc.belegungen.zelle.query(eingabe);
        if (zelleFuer.current === schluessel) setPlanZelle(neu);
      } else {
        setZelle(null);
        const neu = await trpc.wochenraster.zelle.query(eingabe);
        if (zelleFuer.current === schluessel) setZelle(neu);
      }
    } catch (error) {
      if (zelleFuer.current === schluessel) {
        setPlanZelle(null);
        setZellenFehler(klassifiziereFehler(error, { schreibzugriff: false }));
      }
    } finally {
      if (zelleFuer.current === schluessel) setLaedtZelle(false);
    }
  }

  const fehler = listenFehler ?? rasterFehler ?? zellenFehler;
  const nameDerAuswahl =
    auswahl === null
      ? ''
      : (raster?.zeilen.find((zeile) => zeile.employeeId === auswahl.employeeId)?.name ?? '');

  return (
    <section>
      <h2>Wochenraster</h2>
      {fehler === null ? null : <p role="alert">{fehler.meldung}</p>}
      {fehler?.neuAnmelden === true ? (
        <p>
          <a href={anmeldeUrl}>Neu anmelden</a>
        </p>
      ) : null}

      {liste === null ? (
        <p>
          <button
            type="button"
            disabled={laedtListe || gesperrt}
            onClick={() => {
              void oeffnen();
            }}
          >
            {laedtListe ? 'Wird geladen …' : 'Wochenraster öffnen'}
          </button>
        </p>
      ) : !modulAktiv ? (
        <p>Die Ressourcenplanung ist für diesen Mandanten nicht aktiv.</p>
      ) : !darfLesen ? (
        <p>Das Wochenraster ist mit Ihren Rechten nicht verfügbar.</p>
      ) : (
        <>
          <p>
            <label>
              Fachgruppe{' '}
              <select
                value={fenster?.fachgruppeId ?? ''}
                disabled={gesperrt}
                onChange={(event) => {
                  const fachgruppeId = event.target.value;

                  if (fachgruppeId !== '') {
                    void rasterLaden({
                      fachgruppeId,
                      from: fenster?.from ?? currentIsoWeek(new Date(), FACHLICHE_ZEITZONE),
                    });
                  }
                }}
              >
                <option value="">Bitte wählen</option>
                {liste.fachgruppen.map((fachgruppe) => (
                  <option key={fachgruppe.id} value={fachgruppe.id}>
                    {fachgruppe.name}
                  </option>
                ))}
              </select>
            </label>
          </p>
          {fenster === null ? null : (
            <p>
              <button
                type="button"
                disabled={gesperrt}
                onClick={() => {
                  void rasterLaden({ ...fenster, from: fensterVerschieben(fenster.from, -1) });
                }}
              >
                Früher
              </button>{' '}
              ab {wochenkopf(fenster.from, undefined)}{' '}
              <button
                type="button"
                disabled={gesperrt}
                onClick={() => {
                  void rasterLaden({ ...fenster, from: fensterVerschieben(fenster.from, 1) });
                }}
              >
                Später
              </button>
            </p>
          )}
          <p role="status">{laedtRaster ? 'Wird geladen …' : hinweis}</p>
          {raster === null ? null : (
            <WochenrasterTabelle
              raster={raster}
              ausgewaehlt={auswahl}
              gesperrt={laedtRaster || gesperrt}
              onZelle={(ziel) => {
                void zelleLaden(ziel);
              }}
            />
          )}
          {zelle === null ? null : (
            <Aufschluesselung name={nameDerAuswahl} aufschluesselung={zelle} />
          )}
          {planen === undefined || planZelle === null || raster === null ? null : (
            <ZellePlanen
              key={`${planZelle.employeeId}|${String(planZelle.woche.isoYear)}-${String(planZelle.woche.isoWeek)}`}
              name={
                raster.zeilen.find((zeile) => zeile.employeeId === planZelle.employeeId)?.name ?? ''
              }
              zelle={planZelle}
              raster={raster}
              laufendeWoche={currentIsoWeek(new Date(), FACHLICHE_ZEITZONE)}
              nachtragen={planen.nachtragen}
              gesperrt={laedtRaster || laedtZelle || gesperrt}
              warteschlange={planungswarteschlange(planen.inhaber)}
              onGespeichert={(wochen) => {
                void nachAenderung(
                  {
                    employeeId: planZelle.employeeId,
                    isoYear: planZelle.woche.isoYear,
                    isoWeek: planZelle.woche.isoWeek,
                  },
                  wochen,
                );
              }}
            />
          )}
          {darfOffeneAnfragen && fenster !== null && (
            <OffeneAnfragen
              fachgruppeId={fenster.fachgruppeId}
              darfEntscheiden={darfEntscheiden}
              onEntschieden={() => {
                void rasterLaden(fenster);
              }}
            />
          )}
        </>
      )}
    </section>
  );
}
